import { createJSONStorage, persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import { ApiError } from './api.js';
import { markDeleted, mergeLatest, prependOlder, setReaction, upsertMessage, withoutReply, withReply } from './messages.js';
/** List index of the first message; goes down as older pages are added in front (react-virtuoso on the web). */
export const FIRST_INDEX = 1_000_000;
const PAGE = 50;
const TYPING_SHOWN_MS = 6_000;
const TYPING_SEND_EVERY_MS = 3_000;
const RETRY_MS = 4_000;
/**
 * Message history, the outbox, unread counts and "yazıyor…": everything the chat screens read,
 * kept up to date by the server's events. One per signed-in app.
 */
export function createChat({ api, send, storage, uuid = () => crypto.randomUUID() }) {
    const store = createStore()(persist(() => ({ me: null, logs: {}, outbox: [], reads: {}, typing: {}, jump: null, threads: {}, followed: {} }), {
        name: 'novachat.outbox',
        version: 1,
        storage: createJSONStorage(() => storage),
        partialize: (state) => ({ me: state.me, outbox: state.outbox }),
    }));
    const get = store.getState;
    const set = store.setState;
    function updateLog(channelId, change) {
        set((state) => {
            const log = state.logs[channelId];
            if (!log)
                return state;
            return { logs: { ...state.logs, [channelId]: { ...log, ...change(log) } } };
        });
    }
    function updateThread(rootId, change) {
        set((state) => {
            const log = state.threads[rootId];
            if (!log)
                return state;
            return { threads: { ...state.threads, [rootId]: { ...log, ...change(log) } } };
        });
    }
    /** A message's every copy in a channel: its history, and in each open thread there, the replies and the first message. */
    function updateInChannel(channelId, change) {
        updateLog(channelId, (log) => ({ messages: change(log.messages) }));
        set((state) => {
            const threads = Object.fromEntries(Object.entries(state.threads).map(([rootId, log]) => [rootId, log.channelId === channelId ? { ...log, root: change([log.root])[0], messages: change(log.messages) } : log]));
            return { threads };
        });
    }
    /** Replies already counted under their first message: the send's answer and the server's event bring the same one. */
    const countedReplies = new Set();
    /** Called once signed in. Unsent messages of a different account are dropped. */
    function startChat(me) {
        if (get().me !== me)
            set({ me, outbox: [] });
        void flushOutbox();
    }
    function resetChat() {
        set({ me: null, logs: {}, outbox: [], reads: {}, typing: {}, jump: null, threads: {}, followed: {} });
        countedReplies.clear();
    }
    const jumpToMessage = (channelId, messageId) => set({ jump: { channelId, messageId } });
    // ── History ─────────────────────────────────────────────────────────────────
    const loading = new Set();
    /** Loads the latest page, or folds it into what we have (after a reconnect). */
    async function loadLatest(channelId) {
        if (loading.has(channelId))
            return;
        loading.add(channelId);
        try {
            const page = await api.messages(channelId, { limit: PAGE });
            set((state) => {
                const log = state.logs[channelId];
                if (!log) {
                    const next = { messages: page.messages, hasMore: page.hasMore, firstIndex: FIRST_INDEX - page.messages.length, loadingOlder: false };
                    return { logs: { ...state.logs, [channelId]: next } };
                }
                const merged = mergeLatest(log.messages, page);
                const next = merged.reset
                    ? { messages: merged.messages, hasMore: page.hasMore, firstIndex: FIRST_INDEX - merged.messages.length, loadingOlder: false }
                    : { ...log, messages: merged.messages, firstIndex: log.firstIndex - merged.added, hasMore: merged.added > 0 ? page.hasMore : log.hasMore };
                return { logs: { ...state.logs, [channelId]: next } };
            });
        }
        finally {
            loading.delete(channelId);
        }
    }
    async function loadOlder(channelId) {
        const log = get().logs[channelId];
        const first = log?.messages[0];
        if (!log || !first || !log.hasMore || log.loadingOlder)
            return;
        updateLog(channelId, () => ({ loadingOlder: true }));
        try {
            const page = await api.messages(channelId, { before: first.id, limit: PAGE });
            updateLog(channelId, (current) => {
                const { messages, added } = prependOlder(current.messages, page.messages);
                return { messages, hasMore: page.hasMore, firstIndex: current.firstIndex - added, loadingOlder: false };
            });
        }
        catch {
            updateLog(channelId, () => ({ loadingOlder: false }));
        }
    }
    /** Adds or replaces a message wherever it shows. `created`: a new one, so a thread reply counts under its first message (once). */
    function addMessage(message, created = false) {
        set((state) => {
            const mine = message.author?.id === state.me;
            const outbox = mine ? state.outbox.filter((p) => p.clientId !== message.clientId) : state.outbox;
            const log = state.logs[message.channelId];
            const rootId = message.threadRootId;
            if (!rootId) {
                // A channel message; if a thread is open on it or followed, its first message there too.
                const thread = state.threads[message.id];
                const followed = state.followed[message.id];
                return {
                    logs: log ? { ...state.logs, [message.channelId]: { ...log, messages: upsertMessage(log.messages, message) } } : state.logs,
                    threads: thread ? { ...state.threads, [message.id]: { ...thread, root: message } } : state.threads,
                    followed: followed ? { ...state.followed, [message.id]: { ...followed, root: message } } : state.followed,
                    outbox,
                };
            }
            const counted = created && !countedReplies.has(message.id);
            if (counted)
                countedReplies.add(message.id);
            const bump = (m) => (counted ? withReply(m, message) : m);
            const thread = state.threads[rootId];
            const followed = state.followed[rootId];
            return {
                logs: log && counted ? { ...state.logs, [message.channelId]: { ...log, messages: log.messages.map((m) => (m.id === rootId ? bump(m) : m)) } } : state.logs,
                threads: thread ? { ...state.threads, [rootId]: { ...thread, root: bump(thread.root), messages: upsertMessage(thread.messages, message) } } : state.threads,
                followed: followed ? { ...state.followed, [rootId]: { ...followed, root: bump(followed.root) } } : state.followed,
                outbox,
            };
        });
    }
    /** A new reply in a thread you follow: unread unless it is yours (each counted once). */
    function noteReply(message) {
        const rootId = message.threadRootId;
        if (!rootId)
            return;
        set((state) => {
            const mine = message.author?.id === state.me;
            const thread = state.threads[rootId];
            const threads = mine && thread ? { ...state.threads, [rootId]: { ...thread, lastReadMessageId: message.id } } : state.threads;
            const followed = state.followed[rootId];
            if (!followed || (followed.lastReplyId && followed.lastReplyId >= message.id))
                return { threads };
            const mentionsMe = message.mentions.everyone || (state.me !== null && message.mentions.userIds.includes(state.me));
            const next = mine
                ? { ...followed, lastReplyId: message.id, lastReadMessageId: message.id, unread: 0, mentions: 0 }
                : { ...followed, lastReplyId: message.id, unread: Math.min(followed.unread + 1, 100), mentions: Math.min(followed.mentions + (mentionsMe ? 1 : 0), 100) };
            return { threads, followed: { ...state.followed, [rootId]: next } };
        });
    }
    // ── Threads ─────────────────────────────────────────────────────────────────
    /** Opens a thread, or folds its latest replies into what we have (after a reconnect). */
    async function loadThread(rootId) {
        const key = `thread:${rootId}`;
        if (loading.has(key))
            return;
        loading.add(key);
        try {
            const page = await api.thread(rootId, { limit: PAGE });
            set((state) => {
                const log = state.threads[rootId];
                const base = { channelId: page.root.channelId, root: page.root, following: page.following, lastReadMessageId: page.lastReadMessageId, loadingOlder: false };
                if (!log) {
                    return { threads: { ...state.threads, [rootId]: { ...base, messages: page.replies, hasMore: page.hasMore, firstIndex: FIRST_INDEX - page.replies.length } } };
                }
                const merged = mergeLatest(log.messages, { messages: page.replies, hasMore: page.hasMore });
                const next = merged.reset
                    ? { ...base, messages: merged.messages, hasMore: page.hasMore, firstIndex: FIRST_INDEX - merged.messages.length }
                    : { ...log, ...base, messages: merged.messages, firstIndex: log.firstIndex - merged.added, hasMore: merged.added > 0 ? page.hasMore : log.hasMore };
                return { threads: { ...state.threads, [rootId]: next } };
            });
        }
        finally {
            loading.delete(key);
        }
    }
    async function loadOlderInThread(rootId) {
        const log = get().threads[rootId];
        const first = log?.messages[0];
        if (!log || !first || !log.hasMore || log.loadingOlder)
            return;
        updateThread(rootId, () => ({ loadingOlder: true }));
        try {
            const page = await api.thread(rootId, { before: first.id, limit: PAGE });
            updateThread(rootId, (current) => {
                const { messages, added } = prependOlder(current.messages, page.replies);
                return { messages, hasMore: page.hasMore, firstIndex: current.firstIndex - added, loadingOlder: false };
            });
        }
        catch {
            updateThread(rootId, () => ({ loadingOlder: false }));
        }
    }
    /** The thread left the screen: no need to keep it up to date. */
    function closeThread(rootId) {
        set((state) => {
            const { [rootId]: _closed, ...threads } = state.threads;
            return { threads };
        });
    }
    async function refreshThreads() {
        const list = await api.threads();
        set({ followed: Object.fromEntries(list.map((thread) => [thread.root.id, thread])) });
    }
    /** The newest loaded reply counts as read (only a followed thread keeps a read marker). */
    function markThreadRead(rootId) {
        const log = get().threads[rootId];
        const last = log?.messages.at(-1);
        if (!log || !last || !log.following || (log.lastReadMessageId && log.lastReadMessageId >= last.id))
            return;
        updateThread(rootId, () => ({ lastReadMessageId: last.id }));
        set((state) => {
            const followed = state.followed[rootId];
            return followed ? { followed: { ...state.followed, [rootId]: { ...followed, lastReadMessageId: last.id, unread: 0, mentions: 0 } } } : state;
        });
        void api.markThreadRead(rootId, last.id).catch(() => { });
    }
    /** "Takip et" / "Takibi bırak". */
    async function followThread(rootId, on) {
        await api.followThread(rootId, on);
        updateThread(rootId, () => ({ following: on }));
        await refreshThreads().catch(() => { });
    }
    // ── Sending ─────────────────────────────────────────────────────────────────
    /** `threadRootId`: a reply in that thread. */
    function sendMessage(channelId, content, replyTo, attachments = [], threadRootId) {
        const pending = {
            clientId: uuid(),
            channelId,
            ...(threadRootId ? { threadRootId } : {}),
            content,
            replyTo,
            attachments,
            createdAt: new Date().toISOString(),
            failed: false,
        };
        set((state) => ({ outbox: [...state.outbox, pending] }));
        void flushOutbox();
    }
    function retryMessage(clientId) {
        set((state) => ({ outbox: state.outbox.map((p) => (p.clientId === clientId ? { ...p, failed: false } : p)) }));
        void flushOutbox();
    }
    function discardMessage(clientId) {
        set((state) => ({ outbox: state.outbox.filter((p) => p.clientId !== clientId) }));
    }
    /** Offline, overloaded or rate limited: try again later. Anything else is the message's fault. */
    const retryable = (error) => error instanceof ApiError && (error.status === 0 || error.status === 429 || error.status >= 500);
    let flushing = false;
    let retryTimer;
    /** Sends queued messages one by one, so each channel keeps the order they were written in. */
    async function flushOutbox() {
        if (flushing || !get().me)
            return;
        flushing = true;
        clearTimeout(retryTimer);
        try {
            for (;;) {
                const next = get().outbox.find((p) => !p.failed);
                if (!next)
                    return;
                try {
                    const message = await api.sendMessage(next.channelId, {
                        content: next.content,
                        clientId: next.clientId,
                        ...(next.replyTo ? { replyToId: next.replyTo.id } : {}),
                        ...(next.threadRootId ? { threadRootId: next.threadRootId } : {}),
                        ...(next.attachments?.length ? { attachmentIds: next.attachments.map((a) => a.id) } : {}),
                    });
                    addMessage(message, true);
                    noteReply(message);
                }
                catch (error) {
                    if (retryable(error)) {
                        retryTimer = setTimeout(() => void flushOutbox(), RETRY_MS);
                        return;
                    }
                    set((state) => ({ outbox: state.outbox.map((p) => (p.clientId === next.clientId ? { ...p, failed: true } : p)) }));
                }
            }
        }
        finally {
            flushing = false;
        }
    }
    const typingSent = new Map();
    /** Called on every keystroke; tells others at most every few seconds. */
    function notifyTyping(channelId) {
        const now = Date.now();
        if (now - (typingSent.get(channelId) ?? 0) < TYPING_SEND_EVERY_MS)
            return;
        typingSent.set(channelId, now);
        send({ type: 'typing', channelId });
    }
    // ── Actions on messages ─────────────────────────────────────────────────────
    // The server's events update the list, so these only make the call.
    const editMessage = (id, content) => api.editMessage(id, content).then(addMessage);
    /** Applied locally too, in case the socket is down when the server confirms. */
    const deleteMessage = (message) => api.deleteMessage(message.id).then(() => {
        const at = new Date().toISOString();
        // A reply's count under its first message goes down with the server's event.
        updateInChannel(message.channelId, (messages) => markDeleted(messages, message.id, at));
    });
    const pinMessage = (id, on) => api.pin(id, on).then(addMessage);
    function toggleReaction(message, emoji) {
        const me = get().me;
        if (!me)
            return;
        const on = !message.reactions.some((r) => r.emoji === emoji && r.userIds.includes(me));
        // Shown at once; the server's event confirms it, a failure undoes it.
        updateInChannel(message.channelId, (messages) => setReaction(messages, message.id, emoji, me, on));
        api.react(message.id, emoji, on).catch(() => {
            updateInChannel(message.channelId, (messages) => setReaction(messages, message.id, emoji, me, !on));
        });
    }
    // ── Read state ──────────────────────────────────────────────────────────────
    async function refreshReadStates() {
        const states = await api.readStates();
        set({ reads: Object.fromEntries(states.map((s) => [s.channelId, s])) });
    }
    /** The newest loaded message counts as read. */
    function markChannelRead(channelId) {
        const last = get().logs[channelId]?.messages.at(-1);
        const read = get().reads[channelId];
        if (!last || (read?.lastReadMessageId && read.lastReadMessageId >= last.id))
            return;
        set((state) => ({ reads: { ...state.reads, [channelId]: { channelId, lastMessageId: last.id, ...read, lastReadMessageId: last.id, unread: 0, mentions: 0 } } }));
        void api.markRead(channelId, last.id).catch(() => { });
    }
    // ── Server events ───────────────────────────────────────────────────────────
    /** After (re)connecting: catch up on what was missed while offline. */
    function chatReady() {
        void refreshReadStates().catch(() => { });
        void refreshThreads().catch(() => { });
        for (const channelId of Object.keys(get().logs))
            void loadLatest(channelId).catch(() => { });
        for (const rootId of Object.keys(get().threads))
            void loadThread(rootId).catch(() => { });
        void flushOutbox();
    }
    function applyChatEvent(event) {
        switch (event.type) {
            case 'message.created': {
                const { message } = event;
                addMessage(message, true);
                // A thread reply counts in "Dallar", not in its channel.
                if (message.threadRootId)
                    return noteReply(message);
                set((state) => {
                    const mine = message.author?.id === state.me;
                    const mentionsMe = message.mentions.everyone || (state.me !== null && message.mentions.userIds.includes(state.me));
                    const read = state.reads[message.channelId] ?? { channelId: message.channelId, lastReadMessageId: null, lastMessageId: null, unread: 0, mentions: 0 };
                    const typing = { ...state.typing[message.channelId] };
                    if (message.author)
                        delete typing[message.author.id];
                    return {
                        reads: {
                            ...state.reads,
                            [message.channelId]: mine
                                ? { ...read, lastMessageId: message.id, lastReadMessageId: message.id, unread: 0, mentions: 0 }
                                : message.kind === 'system'
                                    ? // "Görüşme başladı" and the like are never unread.
                                        { ...read, lastMessageId: message.id }
                                    : { ...read, lastMessageId: message.id, unread: Math.min(read.unread + 1, 100), mentions: Math.min(read.mentions + (mentionsMe ? 1 : 0), 100) },
                        },
                        typing: { ...state.typing, [message.channelId]: typing },
                    };
                });
                return;
            }
            case 'message.updated':
                addMessage(event.message);
                return;
            case 'message.deleted': {
                const at = new Date().toISOString();
                updateInChannel(event.channelId, (messages) => markDeleted(messages, event.messageId, at));
                const rootId = event.threadRootId;
                if (rootId) {
                    // One reply fewer under its first message, wherever that shows.
                    const fewer = (messages) => messages.map((m) => (m.id === rootId ? withoutReply(m) : m));
                    updateLog(event.channelId, (log) => ({ messages: fewer(log.messages) }));
                    updateThread(rootId, (log) => ({ root: withoutReply(log.root) }));
                    set((state) => {
                        const followed = state.followed[rootId];
                        return followed ? { followed: { ...state.followed, [rootId]: { ...followed, root: withoutReply(followed.root) } } } : state;
                    });
                    if ((get().followed[rootId]?.unread ?? 0) > 0)
                        void refreshThreads().catch(() => { });
                    return;
                }
                // An unread message may be gone; let the server recount.
                if ((get().reads[event.channelId]?.unread ?? 0) > 0)
                    void refreshReadStates().catch(() => { });
                return;
            }
            case 'reaction.added':
            case 'reaction.removed':
                updateInChannel(event.channelId, (messages) => setReaction(messages, event.messageId, event.emoji, event.userId, event.type === 'reaction.added'));
                return;
            case 'typing':
                if (event.userId === get().me)
                    return;
                set((state) => ({
                    typing: { ...state.typing, [event.channelId]: { ...state.typing[event.channelId], [event.userId]: Date.now() + TYPING_SHOWN_MS } },
                }));
                return;
            case 'read.updated':
                set((state) => {
                    const read = state.reads[event.channelId];
                    if (!read || (read.lastReadMessageId && read.lastReadMessageId >= event.lastReadMessageId))
                        return state;
                    const caughtUp = !read.lastMessageId || event.lastReadMessageId >= read.lastMessageId;
                    return {
                        reads: {
                            ...state.reads,
                            [event.channelId]: { ...read, lastReadMessageId: event.lastReadMessageId, unread: caughtUp ? 0 : read.unread, mentions: caughtUp ? 0 : read.mentions },
                        },
                    };
                });
                if (!get().reads[event.channelId]?.unread)
                    return;
                void refreshReadStates().catch(() => { });
                return;
            case 'thread.read': {
                const { rootId, lastReadMessageId } = event;
                updateThread(rootId, (log) => (log.lastReadMessageId && log.lastReadMessageId >= lastReadMessageId ? {} : { lastReadMessageId }));
                set((state) => {
                    const followed = state.followed[rootId];
                    if (!followed || (followed.lastReadMessageId && followed.lastReadMessageId >= lastReadMessageId))
                        return state;
                    const caughtUp = !followed.lastReplyId || lastReadMessageId >= followed.lastReplyId;
                    return {
                        followed: {
                            ...state.followed,
                            [rootId]: { ...followed, lastReadMessageId, unread: caughtUp ? 0 : followed.unread, mentions: caughtUp ? 0 : followed.mentions },
                        },
                    };
                });
                if (get().followed[rootId]?.unread)
                    void refreshThreads().catch(() => { });
                return;
            }
            case 'thread.followed':
                updateThread(event.rootId, () => ({ following: event.following }));
                if (event.following) {
                    void refreshThreads().catch(() => { });
                }
                else {
                    set((state) => {
                        const { [event.rootId]: _gone, ...followed } = state.followed;
                        return { followed };
                    });
                }
                return;
            default:
                return;
        }
    }
    /** Drops a channel the user can no longer see. */
    function forgetChannel(channelId) {
        set((state) => {
            const { [channelId]: _log, ...logs } = state.logs;
            const { [channelId]: _read, ...reads } = state.reads;
            const threads = Object.fromEntries(Object.entries(state.threads).filter(([, log]) => log.channelId !== channelId));
            const followed = Object.fromEntries(Object.entries(state.followed).filter(([, thread]) => thread.root.channelId !== channelId));
            return { logs, reads, threads, followed, outbox: state.outbox.filter((p) => p.channelId !== channelId) };
        });
    }
    return {
        store,
        startChat,
        resetChat,
        jumpToMessage,
        loadLatest,
        loadOlder,
        sendMessage,
        retryMessage,
        discardMessage,
        flushOutbox,
        notifyTyping,
        editMessage,
        deleteMessage,
        pinMessage,
        toggleReaction,
        refreshReadStates,
        markChannelRead,
        chatReady,
        applyChatEvent,
        forgetChannel,
        loadThread,
        loadOlderInThread,
        closeThread,
        refreshThreads,
        markThreadRead,
        followThread,
    };
}
//# sourceMappingURL=chat.js.map